"use client";

import React, { useState, useEffect } from 'react';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { DealCard } from '../components/nft/DealCard';
import { XamanTransactionModal, type TransactionStage } from '../components/transactions/XamanTransactionModal';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import type { TransactionFees, NFTDetails } from '../components/transactions/TransactionConfirmation';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { apiClient } from '../api/client';
import { getDeals, type DealNFT, type DealsSortBy } from '../api/deals';
import { FeatherZap } from '@subframe/core';
import { AnimatedZap } from '../components/ui/AnimatedZap';
import {
  calculateBuyAmount,
  calculateMarketplaceFee,
  getMarketplaceFeePercentage,
  getMarketplaceName,
} from '../lib/marketplace-constants';
import { useCartStore, type CartItem } from '../store/cartStore';
import { featureFlags } from '../config/featureFlags';

export const DealsPage: React.FC = () => {
  // Data state
  const [deals, setDeals] = useState<DealNFT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<DealsSortBy>('xrp');
  const [currentBrokerAddress, setCurrentBrokerAddress] = useState<string | null>(null);
  const [currentOfferIndex, setCurrentOfferIndex] = useState<string | null>(null);

  // Buy Now transaction modal state
  const { user, isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const cartStore = useCartStore();
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionStage, setTransactionStage] = useState<TransactionStage>('confirmation');
  const [transactionFees, setTransactionFees] = useState<TransactionFees | null>(null);
  const [transactionNFTDetails, setTransactionNFTDetails] = useState<NFTDetails | null>(null);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | undefined>(undefined);
  const [txHash, setTxHash] = useState<string | undefined>(undefined);
  const [processing, setProcessing] = useState(false);

  // Load deals on mount, page change, or sort change
  useEffect(() => {
    loadDeals();
  }, [currentPage, sortBy]);

  const loadDeals = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDeals(currentPage, 50, sortBy);

      if (response.success && response.data) {
        if (currentPage === 1) {
          setDeals(response.data.deals);
        } else {
          setDeals(prev => [...prev, ...response.data!.deals]);
        }
        setTotalPages(response.data.pagination.pages);
      } else {
        setError('Failed to load deals');
      }
    } catch (err) {
      console.error('Error loading deals:', err);
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (newSort: DealsSortBy) => {
    if (newSort !== sortBy) {
      setSortBy(newSort);
      setCurrentPage(1);
      setDeals([]);
    }
  };

  // Infinite scroll
  const loadMoreDeals = () => {
    if (currentPage < totalPages && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMoreDeals,
    enabled: currentPage < totalPages && !loading && deals.length > 0,
  });

  // Buy Now handler
  const handleBuyNow = (nft: DealNFT, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAuthenticated || !user?.xrpl_address || !nft.min_sell_price) return;

    // Get broker address and offer ID from the offer
    const brokerAddress = nft.min_sell_destination || null;
    setCurrentBrokerAddress(brokerAddress);
    setCurrentOfferIndex(nft.min_sell_offer_id || null);

    // min_sell_price is in XRP, convert to drops for fee calculation
    const priceInDrops = Math.floor(nft.min_sell_price * 1_000_000);
    const amount = priceInDrops / 1_000_000;
    const platformFee = calculateMarketplaceFee(priceInDrops, brokerAddress) / 1_000_000;
    const totalCost = amount + platformFee;

    setTransactionFees({
      amount,
      platformFee,
      networkFee: 0,
      totalCost,
      isMarketplaceBrokered: !!brokerAddress,
      feePercentage: getMarketplaceFeePercentage(brokerAddress),
      marketplaceName: getMarketplaceName(brokerAddress),
    });

    setTransactionNFTDetails({
      name: nft.name || 'Unknown NFT',
      imageUrl: nft.thumbnail_url || nft.media_url || '',
      collectionName: nft.collection_name || 'Unknown Collection',
      tokenId: nft.nft_token_id
    });

    setTransactionStage('confirmation');
    setTxHash(undefined);
    setShowTransactionModal(true);
  };

  const handleAddToCart = (nft: DealNFT, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!nft.min_sell_price || !nft.min_sell_offer_id) return;

    const priceXrp = Number(nft.min_sell_price);
    const priceInDrops = Math.floor(priceXrp * 1_000_000);

    const cartItem: CartItem = {
      nft_token_id: nft.nft_token_id,
      name: nft.name,
      thumbnail_url: nft.thumbnail_url,
      image_url: nft.media_url,
      collection_name: nft.collection_name,
      collection_slug: null,
      owner_address: '', // DealNFT doesn't have owner_address; backend validates at checkout
      offer_id: nft.min_sell_offer_id,
      price_drops: priceInDrops,
      price_xrp: priceXrp,
      destination: nft.min_sell_destination,
      expiration: null,
      added_at: Date.now(),
      rarity_rank: nft.rarity_rank,
    };

    const added = cartStore.addItem(cartItem);
    if (added) {
      showSuccess('Added to cart');
    } else if (cartStore.isInCart(nft.nft_token_id)) {
      showError('Already in cart');
    } else if (cartStore.items.length >= 20) {
      showError('Cart is full (max 20 items)');
    } else {
      showError('Cannot add to cart');
    }
  };

  const handleConfirmTransaction = async () => {
    if (!transactionNFTDetails || !user?.xrpl_address || !transactionFees) return;

    setProcessing(true);

    try {
      const brokerAddress = currentBrokerAddress;

      if (brokerAddress) {
        // Marketplace-brokered: create matching buy offer
        const nftResponse = await apiClient.get<any>(`/nfts/${transactionNFTDetails.tokenId}`);
        if (!nftResponse.success || !nftResponse.data?.nft) {
          throw new Error('NFT not found');
        }

        const nftOwner = nftResponse.data.nft.owner_address;
        const sellPriceDrops = transactionFees.amount * 1_000_000;
        const buyOfferAmount = calculateBuyAmount(sellPriceDrops, brokerAddress);

        const response = await apiClient.post<any>('/offers/create', {
          nftTokenId: transactionNFTDetails.tokenId,
          offerType: 'buy',
          amount: buyOfferAmount,
          owner: nftOwner,
          userAddress: user.xrpl_address,
          destination: brokerAddress,
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
          setTransactionStage('signing');
        } else {
          throw new Error(response.message || 'Failed to create transaction');
        }
      } else {
        // P2P: directly accept the sell offer
        if (!currentOfferIndex) {
          throw new Error('Offer not found');
        }

        const response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: transactionNFTDetails.tokenId,
          offerIndex: currentOfferIndex,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
          setTransactionStage('signing');
        } else {
          throw new Error(response.message || 'Failed to accept offer');
        }
      }
    } catch (err) {
      console.error('Transaction error:', err);
      showError(err instanceof Error ? err.message : 'Failed to create transaction');
      setShowTransactionModal(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleTransactionSuccess = async (txHash: string) => {
    setTxHash(txHash);
    setTransactionStage('success');
    showSuccess('Purchase successful!');

    // Refresh deals list after successful purchase
    setCurrentPage(1);
    loadDeals();
  };

  const handleTransactionError = (error: string) => {
    showError(error);
    setShowTransactionModal(false);
  };

  const handleCloseModal = () => {
    setShowTransactionModal(false);
    setTransactionStage('confirmation');
    setTransactionFees(null);
    setTransactionNFTDetails(null);
    setXamanPayload(undefined);
    setTxHash(undefined);
    setProcessing(false);
  };

  // Render skeleton cards for loading state
  const renderSkeletonCards = () => (
    <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid-cols-2 mobile:gap-3">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="flex flex-col rounded-md border border-solid border-neutral-border bg-default-background overflow-hidden animate-pulse"
        >
          <div className="w-full aspect-square bg-neutral-200" />
          <div className="p-4 space-y-3">
            <div className="h-5 bg-neutral-200 rounded w-3/4" />
            <div className="h-4 bg-neutral-200 rounded w-1/2" />
            <div className="h-4 bg-neutral-200 rounded w-1/3" />
            <div className="h-6 bg-neutral-200 rounded w-2/3 mt-4" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <AnimatedZap className="h-8 w-8" />
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Deals
            </h1>
          </div>
          <p className="text-body font-body text-subtext-color">
            Discover NFTs listed below their estimated market value. These deals are sorted by the biggest discounts first, helping you find the best opportunities in the marketplace.
          </p>
          {/* Sort Options and Marketplace Selector */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-body font-body text-subtext-color">Sort by:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSortChange('xrp')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    sortBy === 'xrp'
                      ? 'bg-brand-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  Highest XRP Savings
                </button>
                <button
                  onClick={() => handleSortChange('percentage')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    sortBy === 'percentage'
                      ? 'bg-brand-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  Highest % Off
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error state */}
        {error && !loading && (
          <div className="flex w-full items-center justify-center py-8">
            <span className="text-body font-body text-error-700">{error}</span>
          </div>
        )}

        {/* Loading state (first page) */}
        {loading && currentPage === 1 && renderSkeletonCards()}

        {/* Empty state */}
        {!loading && !error && deals.length === 0 && (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <FeatherZap className="h-12 w-12 text-neutral-400" />
            <span className="text-body font-body text-subtext-color">
              No deals available at the moment.
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Check back later for NFTs listed below their estimated value.
            </span>
          </div>
        )}

        {/* Deals Grid */}
        {deals.length > 0 && (
          <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid-cols-2 mobile:gap-3">
            {deals.map((deal) => (
              <DealCard
                key={deal.id}
                nft={deal}
                onBuyClick={handleBuyNow}
                isAuthenticated={isAuthenticated}
                sortBy={sortBy}
                onAddToCart={featureFlags.shoppingCart && deal.min_sell_offer_id ? handleAddToCart : undefined}
                isInCart={featureFlags.shoppingCart && cartStore.isInCart(deal.nft_token_id)}
              />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        {currentPage < totalPages && deals.length > 0 && (
          <div ref={sentinelRef} className="h-4 w-full" />
        )}

        {/* Loading indicator for infinite scroll */}
        {loading && currentPage > 1 && (
          <div className="flex w-full items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}
      </div>

      {/* Buy Now Transaction Modal */}
      {showTransactionModal && transactionNFTDetails && transactionFees && (
        <XamanTransactionModal
          type="buy"
          nft={transactionNFTDetails}
          fees={transactionFees}
          stage={transactionStage}
          onStageChange={setTransactionStage}
          payload={xamanPayload}
          txHash={txHash}
          onConfirm={handleConfirmTransaction}
          onSuccess={handleTransactionSuccess}
          onError={handleTransactionError}
          onClose={handleCloseModal}
        />
      )}
    </DefaultPageLayout>
  );
};
